/**
 * What is left of a person (M2-07).
 *
 * The animals got this in M2-05: something dies, the actor leaves the world, a
 * `Carcass` stays where it fell and rots. `carcassSystem` deliberately refused
 * to do it for players, with a comment saying a corpse on the ground would
 * contradict the rebirth rules. That was right at the time and is wrong now —
 * M4-07 settled it the other way, and emphatically: *"the character's corpse
 * persists where they fell, gear and all — lootable, carryable, buriable"*. The
 * corpse-run to get your own kit back off your own body is the entire reason
 * the cauldron costs anything. Without a corpse, death is a teleport.
 *
 * So a `Corpse` is its own thing rather than a widened `Carcass`, because the
 * two differ in every way that matters:
 *
 * - a carcass **rots away** in three game days; a corpse is **permanent**, and
 *   has to be, because the man who owns it may be an hour's walk away and
 *   naked;
 * - a carcass carries a species id, for butchery it does not have yet; a corpse
 *   carries a **loadout**, which is somebody's actual gear.
 *
 * What is *not* here is the cauldron: the shade-walk, the marks, the rebirth at
 * the Pair Dadeni are all M4-07. This ships the half that has to exist for a
 * playtest to continue past somebody's first death — a body, and a note saying
 * plainly that this is not how death will work.
 */
/**
 * Turn dead players into bodies on the ground.
 *
 * Table-driven over `world.combat` rather than watching for a `died` event, for
 * the reason `carcassSystem` gives: a system that watched events would be a
 * second place that has to learn about every future way of dying. What it
 * watches instead is the one fact always true of a corpse — there is a dead
 * combatant standing on a tile.
 *
 * Note the ordering consequence, which is deliberate: the actor row is removed
 * the tick after death, so a body can be robbed while it is *helpless* and only
 * for that one tick after it is dead. That is the right way round. Rob them
 * first and then decide whether to finish them; the corpse keeps whatever you
 * left, and its owner has to come back for it.
 */
export const corpseSystem = {
    name: 'corpses',
    phase: 'sim',
    run(ctx) {
        for (const id of Object.keys(ctx.world.combat).sort()) {
            const combatant = ctx.world.combat[id];
            const actor = ctx.world.actors[id];
            if (!combatant?.vitals.dead || !actor)
                continue;
            // Animals are `carcassSystem`'s business: they rot, and they carry no
            // gear worth the name.
            if (actor.kind === 'creature')
                continue;
            // The tick is part of the id (M4-07). Under M2-07 a corpse was
            // `corpse:${id}` and the guard below made it idempotent — which was
            // right when nobody came back, and wrong the moment the cauldron did:
            // a character on their second death would have found their *first* body
            // still holding the id, and left no second corpse at all. With the tick
            // in it, a replay of the same tick is still idempotent and two deaths
            // are two bodies, which is what the valley should actually contain.
            const corpseId = `corpse:${id}:${ctx.tick}`;
            if (ctx.world.corpses[corpseId])
                continue;
            ctx.world.corpses[corpseId] = {
                id: corpseId,
                x: actor.x,
                y: actor.y,
                ownerId: id,
                name: actor.name,
                loadout: combatant.loadout,
                vitals: combatant.vitals,
                diedAt: ctx.tick,
                cause: combatant.vitals.deathCause ?? 'wound',
            };
            // The person leaves the world; what they were stays on the ground. Both
            // here, so "there is a body" and "there is no longer a player standing
            // there" are one decision rather than two that can disagree.
            delete ctx.world.actors[id];
            delete ctx.world.combat[id];
            ctx.emit({
                type: 'died',
                actors: [id],
                refs: ['corpse', combatant.vitals.deathCause ?? 'wound'],
                text: `${actor.name} lies still.`,
                audience: 'witnesses',
                at: { x: actor.x, y: actor.y },
            });
        }
    },
};
/** What examining one reads as. Composed, like `describeCarcass`. */
export function describeCorpse(corpse) {
    return `The body of ${corpse.name} lies here.`;
}
/**
 * The body belonging to an actor, wherever it currently lives.
 *
 * Death moves a body from `combat` to `corpses`, and almost nothing that asks
 * "what happened to this person" cares which table it is in. Without this,
 * every such caller has to remember the move — and the ones that forget do not
 * throw, they quietly answer "unhurt", which is the worst possible failure for
 * a question about a corpse.
 */
export function bodyOf(world, actorId) {
    const combatant = world.combat[actorId];
    if (combatant)
        return combatant.vitals;
    for (const corpse of Object.values(world.corpses)) {
        if (corpse.ownerId === actorId)
            return corpse.vitals;
    }
    return null;
}
//# sourceMappingURL=death.js.map